// src/components/sourcex/SourceDocPreviewModal.jsx
//
// The whole visit's source document, on a white page, as it will be exported.
//
// WHY: every question in the editor used to carry its own little grey preview
// box. That made the document three times longer to scroll, showed one field
// at a time (never the page), and still couldn't answer the question people
// actually had — "what does the thing I'm about to approve look like?". This
// replaces all of them with one button that renders the real document.
//
// It is the SAME `buildSourceDocHtml` the Print / Save-as-PDF flow uses, in an
// iframe rather than a new tab, so the preview cannot drift from the export:
// there is only one renderer. Wide tables split into stacked sub-tables here
// exactly as they do in Word, because that splitting now lives in the shared
// HTML builder too.
//
// Read-only. It renders the template and writes nothing — not even the export
// header config, which only the Export modal persists.

import { useEffect, useMemo, useRef, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { X, Eye, FileDown, Loader2 } from "lucide-react";
import { db } from "../../firebase";
import { getStudiesPath } from "../../services/firestorePaths";
import {
  buildSourceDocHtml,
  defaultExportConfig,
  exportStudyIdFor,
  footerDateFor,
} from "../../services/sourceDocExport";
import {
  downloadSourceDocx,
  sourceDocxFilename,
} from "../../services/sourceDocDocx";

const todayStr = () => {
  const d = new Date();
  const mon = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ][d.getMonth()];
  return `${String(d.getDate()).padStart(2, "0")}-${mon}-${d.getFullYear()}`;
};

/**
 * @param {string} props.studyId
 * @param {object} props.template  the assembled template being edited
 * @param {object} props.event     the visit to render (the one being edited)
 * @param {() => void} props.onClose
 */
export default function SourceDocPreviewModal({ studyId, template, event, onClose }) {
  const [config, setConfig] = useState(null);
  const frameRef = useRef(null);

  // Same identifiers the real export uses, so the previewed header is the
  // header that prints — read-only here; editing them stays in Export/Print.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Same lookup the Export panel uses, so the previewed header is the one
      // that prints — including for a built (`__assembled`) template, whose id
      // is not a study id. See exportStudyIdFor.
      const realStudyId = exportStudyIdFor(template, studyId);
      let study = {};
      try {
        const snap = await getDoc(doc(db, getStudiesPath(), realStudyId));
        if (snap.exists()) study = snap.data();
      } catch (_) {
        /* defaults are fine if the study doc can't be read — this is a preview */
      }
      if (cancelled) return;
      setConfig(defaultExportConfig(study, template?.exportConfig || {}, todayStr()));
    })();
    return () => {
      cancelled = true;
    };
  }, [studyId, template]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // The footer date is the date THIS VISIT was approved, derived from the
  // visit rather than stored — the same rule the export uses, so the preview
  // keeps showing the document that prints. A visit never approved previews
  // as a draft dated today. See footerDateFor.
  const previewConfig = useMemo(
    () => (config ? { ...config, date: footerDateFor(event) } : null),
    [config, event]
  );

  const html = useMemo(
    () =>
      previewConfig && event
        ? buildSourceDocHtml({
            event,
            config: previewConfig,
            // The page's own floating button would sit on top of this modal's
            // toolbar, which already has one.
            showPrintButton: false,
            // Real letter pages, each with its header, footer and "Page N of
            // M" — the exported document has pages, so the preview of it does.
            paginate: true,
          })
        : "",
    [previewConfig, event]
  );

  const [downloading, setDownloading] = useState(false);
  const download = async () => {
    if (!event || !previewConfig || downloading) return;
    setDownloading(true);
    try {
      await downloadSourceDocx({
        event,
        config: previewConfig,
        filename: sourceDocxFilename(event, previewConfig),
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to build .docx", err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Source document preview"
      className="fixed inset-0 z-[80] flex flex-col bg-black/80 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="shrink-0 flex flex-wrap items-center gap-2 px-4 py-3 border-b border-white/10 bg-[#0c0826]/95">
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-white">
          <Eye className="w-4 h-4 text-purple-300" />
          Preview — {event?.eventName || "visit"}
        </span>
        <span className="text-[11px] text-slate-400">
          Page for page, exactly what the Word export produces.
        </span>
        <div className="ml-auto flex items-center gap-2">
          {/* Download, not Print — the .docx is what people actually take
              away from here, and this preview exists to check it before they
              do. Print / Save as PDF is still on the Export panel. */}
          <button
            type="button"
            onClick={download}
            disabled={!html || downloading}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-400/10 border border-emerald-400/30 hover:bg-emerald-400/20 disabled:opacity-40 text-xs text-emerald-100 transition"
          >
            {downloading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <FileDown className="w-3.5 h-3.5" />
            )}
            {downloading ? "Building…" : "Download .docx"}
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close preview"
            className="w-8 h-8 rounded-xl flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {html ? (
        <iframe
          ref={frameRef}
          title="Source document preview"
          srcDoc={html}
          className="flex-1 w-full border-0 bg-[#6b7280]"
        />
      ) : (
        <div className="flex-1 flex items-center justify-center gap-2 text-slate-400 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Building the document…
        </div>
      )}
    </div>
  );
}
